import React, { type CSSProperties } from 'react';

import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import { EC_ACCOUNT_PLATFORM_COLORS } from '../../../constants/ecAccountPlatforms';

interface ModuleNameTagProps {
  moduleName?: string | null;
  style?: CSSProperties;
}

// 電商平台 Tag（顏色取自 EC_ACCOUNT_PLATFORM_COLORS，跨頁面共用）
export const ModuleNameTag: React.FC<ModuleNameTagProps> = ({ moduleName, style }) => {
  if (!moduleName) return null;
  const color =
    (EC_ACCOUNT_PLATFORM_COLORS as Record<string, string | undefined>)[moduleName] || 'geekblue';
  return <Tag color={color} style={style}>{moduleName}</Tag>;
};

interface ConnectionStatusTagProps {
  status?: string;
  i18nKeyPrefix?: string;
  style?: CSSProperties;
}

// 連線狀態 Tag（success/error/warning 三色語義；i18nKey 允許不同頁面使用各自的 namespace）
export const ConnectionStatusTag: React.FC<ConnectionStatusTagProps> = ({
  status,
  i18nKeyPrefix = 'enums.ec_account.connection_status',
  style,
}) => {
  const { t } = useTranslation();
  const config: Record<string, { color: string; key: string }> = {
    connected: { color: 'success', key: 'connected' },
    disconnected: { color: 'error', key: 'disconnected' },
    never_tested: { color: 'warning', key: 'never_tested' },
  };
  const conf = (status && config[status]) || config.never_tested;
  return <Tag color={conf.color} style={style}>{t(`${i18nKeyPrefix}.${conf.key}`)}</Tag>;
};

interface EnabledStatusTagProps {
  value?: boolean | null;
  trueText?: string;
  falseText?: string;
  style?: CSSProperties;
}

// 開啟/關閉 Tag（綠色 ✓ / 灰色 ✗，與 List 既有 StatusBadge 視覺一致）
export const EnabledStatusTag: React.FC<EnabledStatusTagProps> = ({
  value,
  trueText,
  falseText,
  style,
}) => {
  const { t } = useTranslation();
  const finalTrueText = trueText ?? t('enums.ec_account.status.active');
  const finalFalseText = falseText ?? t('enums.ec_account.status.inactive');
  return (
    <Tag
      color={value ? 'success' : 'default'}
      icon={value ? <CheckOutlined /> : <CloseOutlined />}
      style={style}
    >
      {value ? finalTrueText : finalFalseText}
    </Tag>
  );
};
